import { tableGrid } from '@w/content-blocks/src/table-html'
import { wordBoundary } from './chars'
import type { CorePoint, CoreSelection, CoreState } from './state'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { children, gapOf, lastOf, locate, plain, pointAt, rows, type Row } from './tree'
import { isAtom } from './tree-read'

export function formatAt(row: Row, offset: number): number {
  const point = pointAt(row, offset)
  let format = 0
  const visit = (block: Block) => {
    if (block.id === point.blockId && block.type === 'chars') format = block.format ?? 0
    children(block).forEach((block) => { visit(block) })
  }
  visit(row.block)
  return format
}

/**
 * The inline format bits a toolbar shows pressed: for a caret its pending format, else its leaf's; for a range the bits
 * every selected text run has (moved from core-view so the Lexical-free views share it).
 */
export function activeFormats(state: CoreState): number {
  const selection = state.selection
  if (!selection) return 0
  const entries = rows(state.blocks)
  const [start, end] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
    .toSorted((a, b) => a.row - b.row || a.offset - b.offset)
  if (start.row === end.row && start.offset === end.offset) {
    // A chars leaf lives in a text row: the caret's row is searched, not the page.
    let leaf: Block | undefined
    const find = (items: Blocks) => {
      for (const block of items) {
        if (block.id === selection.focus.blockId) leaf = block
        else find(children(block))
      }
    }
    const row = entries[start.row] as Row | undefined
    if (row) find([row.block])
    return state.pendingFormat ?? (leaf?.type === 'chars' ? leaf.format : 0) ?? 0
  }
  const formats: number[] = []
  entries.forEach((entry, index) => {
    if (index < start.row || index > end.row) return
    let offset = 0
    const visit = (items: Blocks) => {
      for (const block of items) {
        if (block.type === 'chars' || block.type === 'text') {
          const next = offset + plain(block).length
          if (next > (index === start.row ? start.offset : 0) && offset < (index === end.row ? end.offset : Infinity))
            formats.push(block.type === 'chars' ? block.format ?? 0 : 0)
          offset = next
        } else if (block.type === 'br' || isAtom(block)) offset += plain(block).length
        else visit(children(block))
      }
    }
    visit(children(entry.block))
  })
  return formats.reduce((mask, format) => mask & format, formats[0] ?? 0)
}

export function move(
  entries: Row[], position: { row: number; offset: number }, backward: boolean, unit: string,
) {
  const { row, offset } = position
  const text = plain(entries[row].block)
  if (unit === 'vertical') {
    const start = offset === 0 ? 0 : text.lastIndexOf('\n', offset - 1) + 1
    const column = offset - start
    const end = text.indexOf('\n', offset)
    if (backward && start > 0) {
      const previous = start < 2 ? 0 : text.lastIndexOf('\n', start - 2) + 1
      return { row, offset: Math.min(previous + column, start - 1) }
    }
    if (!backward && end !== -1) {
      const nextEnd = text.indexOf('\n', end + 1)
      return { row, offset: Math.min(end + 1 + column, nextEnd === -1 ? text.length : nextEnd) }
    }
    const nextRow = row + (backward ? -1 : 1)
    if (nextRow < 0 || nextRow >= entries.length) return { row, offset: backward ? 0 : text.length }
    const next = plain(entries[nextRow].block)
    const lineStart = backward ? next.lastIndexOf('\n') + 1 : 0
    const lineEnd = backward || !next.includes('\n') ? next.length : next.indexOf('\n')
    return { row: nextRow, offset: Math.min(lineStart + column, lineEnd) }
  }
  if (unit === 'document') return backward ? { row: 0, offset: 0 }
    : { row: entries.length - 1, offset: plain(lastOf(entries).block).length }
  if (unit === 'line') return { row, offset: backward
    ? (offset === 0 ? 0 : text.lastIndexOf('\n', offset - 1) + 1)
    : text.includes('\n', offset) ? text.indexOf('\n', offset) : text.length }
  if (backward && offset === 0) return row === 0 ? position
    : { row: row - 1, offset: plain(entries[row - 1].block).length }
  if (!backward && offset === text.length) return row === entries.length - 1 ? position
    : { row: row + 1, offset: 0 }
  if (unit === 'word') return { row, offset: wordBoundary(text, offset, backward) }
  const segments = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)]
  const boundaries = [...segments.map((segment) => segment.index), text.length]
  return { row, offset: backward
    ? boundaries.findLast((boundary) => boundary < offset) ?? 0
    : boundaries.find((boundary) => boundary > offset) ?? text.length }
}

/**
 * A pointer extension from `anchor` into another cell of the anchor cell's table (Lexical `TableSelection` of a
 * shift-click or drag): the cells' rectangle, from the anchor cell's start to the focus cell's end. Null when the points
 * share a cell or do not sit in cells of one table.
 */
export function tableCellRange(blocks: Blocks, anchor: CorePoint, focus: CorePoint): CoreSelection | null {
  const entries = rows(blocks)
  const cell = (point: CorePoint) => {
    if (entries.length === 0 || gapOf(blocks, point)) return null
    const row = entries[locate(entries, point).row]
    const table = row.block.type === 'td' ? row.parents.findLast((block) => block.type === 'table') : undefined
    return table ? { row, table } : null
  }
  const from = cell(anchor)
  const to = cell(focus)
  if (!from || !to || from.table !== to.table || from.row.block === to.row.block) return null
  const grid = tableGrid(from.table)
  const coordinate = (block: Block) => {
    const y = grid.findIndex((cells) => cells.some((candidate) => candidate.id === block.id))
    return { x: grid[y].findIndex((candidate) => candidate.id === block.id), y }
  }
  return { anchor: pointAt(from.row, 0), focus: pointAt(to.row, plain(to.row.block).length),
    table: { tableId: from.table.id, anchor: coordinate(from.row.block), focus: coordinate(to.row.block) } }
}

/**
 * Shift+ArrowUp/Down whose focus sits at a top-level gap (the Lexical root element point) and whose line move lands in
 * a table cell selects that whole table, first cell to last (@lexical/table `$handleArrowKey`, root focus branch); the
 * anchor stays, the focus is the last cell's end. Null when the focus is at no top-level gap or `target` in no table.
 */
export function extendIntoTable(blocks: Blocks, selection: CoreSelection, target: CorePoint): CoreSelection | null {
  if (selection.table || selection.nodes?.length || gapOf(blocks, selection.focus)?.siblings !== blocks) return null
  const entries = rows(blocks)
  if (entries.length === 0 || gapOf(blocks, target)) return null
  const row = entries[locate(entries, target).row]
  const table = row.block.type === 'td' ? row.parents.findLast((block) => block.type === 'table') : undefined
  const cells = table ? rows([table]) : []
  const [first, last] = [cells[0], cells.at(-1)]
  const whole = first && last && tableCellRange(blocks, pointAt(first, 0), pointAt(last, 0))
  return whole ? { ...whole, anchor: selection.anchor } : null
}

/** A range crossing a table boundary covers every cell in that table. */
export function expandTableRange(blocks: Blocks, selection: CoreSelection) {
  if (selection.table || selection.nodes?.length) return selection
  const entries = rows(blocks)
  if (entries.length === 0) return selection
  const owner = (point: CorePoint) => gapOf(blocks, point) ? undefined
    : entries[locate(entries, point).row].parents.find((block) => block.type === 'table')
  const anchorTable = owner(selection.anchor)
  const focusTable = owner(selection.focus)
  const anchorPosition = locate(entries, selection.anchor)
  const focusPosition = locate(entries, selection.focus)
  if ((anchorTable && anchorPosition.offset > 0 &&
    anchorPosition.offset < plain(entries[anchorPosition.row].block).length) ||
    (focusTable && focusPosition.offset > 0 &&
      focusPosition.offset < plain(entries[focusPosition.row].block).length)) return selection
  if (anchorTable === focusTable) {
    if (anchorTable?.type === 'table') {
      const anchor = entries[locate(entries, selection.anchor).row].block
      const focus = entries[locate(entries, selection.focus).row].block
      if (anchor !== focus) {
        const grid = tableGrid(anchorTable)
        const coordinate = (block: Block) => {
          const y = grid.findIndex((row) => row.some((cell) => cell.id === block.id))
          return { x: grid[y].findIndex((cell) => cell.id === block.id), y }
        }
        return { ...selection, table: { tableId: anchorTable.id,
          anchor: coordinate(anchor), focus: coordinate(focus) } }
      }
    }
    return selection
  }
  // A document-level gap sits half a block before its index; any other point at its top-level block.
  const order = (point: CorePoint) => {
    const gap = gapOf(blocks, point)
    if (gap?.siblings === blocks) return gap.index - 0.5
    const row = entries[locate(entries, point).row]
    return blocks.indexOf(row.parents[0] ?? row.block)
  }
  const backward = order(selection.anchor) > order(selection.focus)
  const edge = (table: Block, end: boolean) => {
    const cells = rows([table])
    const cell = end ? lastOf(cells) : cells[0]
    return pointAt(cell, end ? plain(cell.block).length : 0)
  }
  return { ...selection,
    anchor: anchorTable ? edge(anchorTable, backward) : selection.anchor,
    focus: focusTable ? edge(focusTable, !backward) : selection.focus }
}
